import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    fallbackGaiaPayloadProps,
    getExperimentName,
    getFallbackPartner,
    getSupportedLineOfBusiness,
    useGaiaStore,
    useGaiaWebsocket,
} from "@expert/gaia";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useMemo } from "react";
import type { SendMessageProps } from "../shared-types";
import { addFailureMessage, addMessage, useTimelineStore } from "../state";

export const useSendExpertAssistMessage = (
    loggerProp: Logger,
    isExternal: boolean,
    gaiaPayloadProps: GaiaPayloadConfigs,
) => {
    const websocketObj = useGaiaWebsocket();
    const { subscriptionStatus } = useGaiaStore();
    const { dispatcher } = useReactAnalytics();
    const {
        features: { isReactiveCallContextEnabled, isReactiveCallContextV2Enabled, isSalesFAQEnabled },
    } = useFeatures<TimelineFeatures>();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, lob, partner } = getTimelineState(sessionId);

    const logger = useMemo(() => loggerProp.child({ module: "useSendExpertAssistMessage" }), [loggerProp]);

    const sendMessage = ({ id, metadata, text, type, source }: SendMessageProps) => {
        const messageId = id ?? crypto.randomUUID();
        const correlationId = crypto.randomUUID();
        const formattedPartnerForGaia = getFallbackPartner({ partner });
        const { areExperimentsEnabled, appName } = gaiaPayloadProps ?? fallbackGaiaPayloadProps;
        const experimentName = areExperimentsEnabled
            ? getExperimentName({
                  isReactiveCallContextEnabled,
                  isReactiveCallContextV2Enabled,
                  isSalesFAQEnabled,
                  lob,
              })
            : undefined;

        const lineOfBusiness = getSupportedLineOfBusiness({ partner: formattedPartnerForGaia, lob });
        const payload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName: appName,
            message: text,
            messageId,
            messageType: "Search",
            partner: formattedPartnerForGaia,
            sessionId: chatId ?? callSid ?? sessionId,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
            correlationId,
            experimentName,
            lineOfBusiness,
        };

        const messageText = metadata?.helpMeNowText ?? text;
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type,
            isUnread: false,
            metadata,
            status: "active",
        });

        const analyticsInfo = {
            expertAssistAppName: "expert-assist",
            expertAssistExperimentName: experimentName,
            partner,
            messageId,
            messageType: type,
            source,
        };

        if (!websocketObj) {
            logger.error({ payload, websocketObj }, "There is no websocket obj");
            addFailureMessage(sessionId, "connectionFailure");
            return;
        }

        if (subscriptionStatus[sessionId] !== "subscribed") {
            // TODO: Currently, this attempts to resubscribe + sends error message
            // Should we attempt to resubscribe + await + resend message?
            void websocketObj.subscribeSessionToGaia({
                sessionId,
                chatId,
                callSid,
                partner: formattedPartnerForGaia,
                sendJsonMessage: websocketObj.sendJsonMessage,
                logger,
            });
            logger.warn("Expert session not subscribed, subscribing now");
            addFailureMessage(sessionId, "connectionFailure");
            return;
        }
        websocketObj.sendJsonMessage(payload);
        void dispatcher.dispatchBusinessEvent("MessageSent", {
            senderType: "expert",
            ...analyticsInfo,
        });
        logger.info({ payload }, "Message sent to gaia");
        trackFullStoryEvent("ExpertAssistMessageSent", {
            sessionId,
            callSid,
            chatId,
            ...analyticsInfo,
        });
    };
    return sendMessage;
};
